
import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import * as ReactRouterDOM from 'react-router-dom';
import { Menu, X, Settings, Edit, Facebook, MapPin, Edit2, ShoppingCart, Trash2, CreditCard, Truck, Box, Store, AlertTriangle, FileText, DownloadCloud, User, Mail, Phone, ShoppingBag, ChevronDown, Lock, LogOut, ArrowRight, ArrowLeft, Heart } from 'lucide-react';
import EditControls from './EditControls';
import ImagePickerModal from './ImagePickerModal';
import ProjectManager from './ProjectManager';
import AIAssistant from './AIAssistant';
import AiSmartSearch from './AiSmartSearch';
import { AiCartAdvisor } from './AiCartAdvisor';
import CelebrationBanner from './CelebrationBanner';
import AdaptiveLogo from './AdaptiveLogo';
import { SectionType, DeliveryMethodType } from '../types';
import { createPaymentOrder, BillingDetails, ShippingDetails } from '../services/paymentService';

const { Link, useLocation, useNavigate } = ReactRouterDOM as any;

interface LayoutProps {
  children: React.ReactNode;
}

const FREE_SHIPPING_THRESHOLD = 500;
const SHIPPING_COST_COURIER = 20;
const SHIPPING_COST_PACZKOMAT = 15;
const TAX_RATE = 0.23;

type DocumentType = 'receipt' | 'receipt_nip' | 'invoice';

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { 
      pages, config, isEditMode, toggleEditMode, updateConfig, viewMode,
      cart, isCartOpen, toggleCart, removeFromCart, clearCart, isLoading,
      isAdminLoggedIn, logout
  } = useApp();
  
  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [isProjectManagerOpen, setIsProjectManagerOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  
  // Cart & Delivery State
  const [checkoutStep, setCheckoutStep] = useState<0 | 1>(0); // 0 = Items, 1 = Form
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethodType>('courier');
  const [paczkomatPoint, setPaczkomatPoint] = useState<{name: string, address?: string} | null>(null);
  
  const [shippingData, setShippingData] = useState<ShippingDetails>({
      firstName: '', lastName: '', email: '', phone: '', street: '', city: '', zipCode: ''
  });

  const [documentType, setDocumentType] = useState<DocumentType>('receipt');
  const [isFetchingGus, setIsFetchingGus] = useState(false);
  const [invoiceData, setInvoiceData] = useState({ nip: '', companyName: '', address: '' });
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    // Use relative path for default favicon too
    if (config.faviconUrl) link.href = config.faviconUrl;
    else link.href = 'vite.svg'; 
  }, [config.faviconUrl]);

  // Reset checkout step when cart closes
  useEffect(() => {
      if (!isCartOpen) setCheckoutStep(0);
  }, [isCartOpen]);

  const oversizedItems = cart.filter(item => item.shippingSize === 'courier_only');
  const isPaczkomatDisabled = oversizedItems.length > 0;

  useEffect(() => {
      if (isPaczkomatDisabled && deliveryMethod === 'paczkomat') setDeliveryMethod('courier');
  }, [cart, isPaczkomatDisabled, deliveryMethod]);

  const handleLogoEdit = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsLogoModalOpen(true);
  };

  const handleLogoSave = (newUrl: string | string[]) => {
    updateConfig({ logoUrl: Array.isArray(newUrl) ? newUrl[0] : newUrl });
    setIsLogoModalOpen(false);
  };
  
  const handleNavClick = (slug: string, sectionId?: string) => {
      setIsMobileMenuOpen(false); setActiveDropdown(null);
      if (sectionId) {
          navigate(slug);
          setTimeout(() => {
              const element = document.getElementById(sectionId);
              if (element) element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
      } else navigate(slug);
  };

  const fetchCompanyData = async () => {
      if (!invoiceData.nip || invoiceData.nip.length < 9) { alert('Wpisz poprawny numer NIP.'); return; }
      setIsFetchingGus(true);
      try {
          const res = await fetch(`api/gus.php?nip=${invoiceData.nip}`);
          if (res.ok) {
              const data = await res.json();
              if (data.success) {
                  setInvoiceData(prev => ({ ...prev, companyName: data.companyName, address: data.address || prev.address }));
                  setIsFetchingGus(false); return;
              }
          }
      } catch (e) {}
      setTimeout(() => {
          setInvoiceData(prev => ({ ...prev, companyName: 'Envicon Polska Sp. z o.o. (Symulacja)', address: 'ul. Rdestowa 51, 81-577 Gdynia' }));
          setIsFetchingGus(false);
      }, 1500);
  };

  const cartSubtotalGross = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  let discountPercent = 0;
  if (cartCount >= 3) {
      discountPercent = 15;
  } else if (cartCount === 2) {
      discountPercent = 10;
  }
  
  const discountAmount = (cartSubtotalGross * discountPercent) / 100;
  const cartSubtotalAfterDiscount = cartSubtotalGross - discountAmount;

  const isFreeShipping = cartSubtotalAfterDiscount >= FREE_SHIPPING_THRESHOLD;
  const getShippingCost = () => {
      if (deliveryMethod === 'pickup') return 0;
      if (isFreeShipping) return 0;
      return deliveryMethod === 'courier' ? SHIPPING_COST_COURIER : SHIPPING_COST_PACZKOMAT;
  };
  const shippingCostGross = getShippingCost();
  const totalAmountGross = cartSubtotalAfterDiscount + shippingCostGross;

  const openGeowidget = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const win = window as any;
      if (win.openInPostGeowidgetModal) {
          try {
              win.openInPostGeowidgetModal({
                  token: config.inPostGeowidgetToken || 'TEST_TOKEN', 
                  onPoint: (point: any) => setPaczkomatPoint({ name: point.name, address: `${point.address_details.street} ${point.address_details.building_number}, ${point.address_details.city}` })
              });
          } catch (e) { alert("Błąd mapy InPost."); }
      } else alert('Skrypt InPost nie załadowany.');
  };

  const handleCheckout = async () => {
      if (cart.length === 0) return;
      if (!shippingData.firstName || !shippingData.lastName || !shippingData.email) { alert('Uzupełnij dane kontaktowe.'); return; }
      if (deliveryMethod === 'courier' && !shippingData.street) { alert('Adres wymagany dla kuriera.'); return; }
      if (deliveryMethod === 'paczkomat' && !paczkomatPoint) { alert('Wybierz Paczkomat.'); return; }
      
      setIsPaymentProcessing(true);
      const deliveryDetails = { method: deliveryMethod, cost: shippingCostGross, pointName: paczkomatPoint?.name, pointAddress: paczkomatPoint?.address };
      const billingDetails: BillingDetails = { nip: invoiceData.nip, companyName: documentType === 'invoice' ? invoiceData.companyName : '', address: documentType === 'invoice' ? invoiceData.address : '' };

      try {
          const res = await createPaymentOrder(cart, totalAmountGross, deliveryDetails, documentType !== 'receipt' ? billingDetails : undefined, shippingData);
          if (res.success) { clearCart(); toggleCart(); navigate('/dziekujemy'); } 
          else alert('Błąd płatności.');
      } catch (error) { alert('Błąd połączenia.'); } finally { setIsPaymentProcessing(false); }
  };

  // Nav Logic
  const sortedPages = [...pages].sort((a, b) => {
    if (a.slug === '/') return -1;
    if (b.slug === '/') return 1;
    return a.title.localeCompare(b.title);
  });
  const legalSlugs = ['/regulamin', '/polityka-prywatnosci', '/kontakt'];
  const visibleHeaderPages = sortedPages.filter(p => !['/regulamin', '/polityka-prywatnosci'].includes(p.slug));
  const leftNavPages = visibleHeaderPages.filter(p => p.slug === '/' || p.slug === '/kolekcja');
  const rightNavPages = visibleHeaderPages.filter(p => p.slug !== '/' && p.slug !== '/kolekcja');

  const renderNavItem = (page: typeof pages[0]) => {
      const productSections = page.sections.filter(s => s.type === SectionType.PRODUCT_GRID && s.title);
      const hasDropdown = productSections.length > 1;
      const isActive = location.pathname === page.slug;

      if (!hasDropdown) {
          return <Link key={page.id} to={page.slug} className={`text-xs font-medium uppercase tracking-[0.15em] transition-colors hover:text-black relative py-2 ${isActive ? 'text-black' : 'text-gray-500'}`}>{page.title}</Link>;
      }
      return (
          <div key={page.id} className="relative group" onMouseEnter={() => setActiveDropdown(page.id)} onMouseLeave={() => setActiveDropdown(null)}>
              <Link to={page.slug} className={`text-xs font-medium uppercase tracking-[0.15em] transition-colors hover:text-black relative py-2 flex items-center gap-1 ${isActive ? 'text-black' : 'text-gray-500'}`}>{page.title} <ChevronDown size={10} /></Link>
              <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 w-56 transition-all duration-300 ${activeDropdown === page.id ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                  <div className="bg-white border border-gray-100 shadow-xl p-4 flex flex-col gap-2 rounded-sm relative">
                       <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-t border-l border-gray-100 rotate-45"></div>
                       {productSections.map(section => (
                           <button key={section.id} onClick={(e) => { e.stopPropagation(); handleNavClick(page.slug, section.id); }} className="text-left text-[10px] uppercase tracking-widest text-gray-500 hover:text-black hover:bg-gray-50 p-2 transition-colors">{section.title}</button>
                       ))}
                  </div>
              </div>
          </div>
      );
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-[#fcfcfc]"><p className="text-xs uppercase tracking-widest text-gray-500">Ładowanie...</p></div>;

  return (
    <div className="flex flex-col min-h-screen text-gray-800 bg-[#fcfcfc]">
      
      {/* ADMIN BAR */}
      {isAdminLoggedIn && (
        <div className="bg-black text-white p-2 text-xs flex justify-between items-center sticky top-0 z-50 shadow-md">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-gray-300 font-medium">
               <Settings size={14} />
               <span className="tracking-wide hidden sm:inline">Panel Sklepu (Live)</span>
            </div>
            <button onClick={() => setIsProjectManagerOpen(true)} className="flex items-center space-x-1 bg-gray-800 hover:bg-gray-700 text-gray-400 px-3 py-1 rounded transition-colors">
              <span>Zapisz / Eksport</span>
            </button>
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center cursor-pointer space-x-2 bg-gray-800 py-1 px-3 rounded-full border border-gray-700 hover:border-gray-500 transition-colors select-none">
               <span className={`uppercase font-bold tracking-wider ${isEditMode ? "text-green-400" : "text-gray-400"}`}>{isEditMode ? 'Edycja' : 'Podgląd'}</span>
               <button onClick={toggleEditMode} className={`p-1 rounded-full transition-colors ${isEditMode ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}><Edit size={12} /></button>
            </label>
            <button onClick={logout} className="text-red-400 hover:text-red-300 ml-2" title="Wyloguj"><LogOut size={16}/></button>
          </div>
        </div>
      )}

      {/* PROMO BANNER */}
      {!isAdminLoggedIn && (
         <div className="bg-black text-white text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] py-2 text-center sticky top-0 z-50 px-4">
            Darmowa dostawa od {FREE_SHIPPING_THRESHOLD} zł! Sprawdź nasze opcje.
         </div>
      )}

      {/* HEADER */}
      <header className={`bg-white/90 backdrop-blur-md sticky z-40 transition-colors border-b border-gray-100 ${isAdminLoggedIn ? 'top-[38px]' : 'top-[32px]'}`}>
        <div className="container mx-auto px-6 py-6 flex justify-between items-center">
          <nav className="hidden md:flex space-x-8 w-1/3 items-center">{leftNavPages.map(page => renderNavItem(page))}</nav>
          <div className="relative group w-1/3 flex justify-center">
            <Link to="/" className="flex flex-col items-center justify-center text-primary hover:opacity-90 transition-opacity">
              {config.logoUrl ? <img src={config.logoUrl} alt={config.companyName} className="h-12 w-auto object-contain" /> : <div className="flex flex-col items-center"><div className="h-10 w-10 mb-1"><AdaptiveLogo /></div><span className="text-2xl font-serif tracking-widest text-black leading-none">{config.companyName}</span></div>}
            </Link>
            {isEditMode && isAdminLoggedIn && <button onClick={handleLogoEdit} className="absolute top-0 right-10 bg-gray-100 text-gray-600 p-2 rounded-full shadow-sm hover:bg-gray-200"><Edit2 size={12} /></button>}
          </div>
           <nav className="hidden md:flex space-x-8 w-1/3 justify-end items-center">
              {rightNavPages.map(page => renderNavItem(page))}
                <AiSmartSearch />
                <Link to="/ulubione" className="text-gray-500 hover:text-red-500 transition-colors hidden sm:block">
                     <Heart size={20} strokeWidth={1.5} />
                </Link>
                <button onClick={toggleCart} className="relative text-gray-500 hover:text-black transition-colors p-2"><ShoppingCart size={20} strokeWidth={1.5} />{cartCount > 0 && <span className="absolute top-0 right-0 bg-black text-white text-[9px] font-medium w-4 h-4 rounded-full flex items-center justify-center transform translate-x-1 -translate-y-1">{cartCount}</span>}</button>
           </nav>
            <div className="md:hidden flex items-center gap-4">
                 <button onClick={toggleCart} className="relative text-gray-500"><ShoppingCart size={20} strokeWidth={1.5} />{cartCount > 0 && <span className="absolute top-0 right-0 bg-black text-white text-[9px] font-medium w-4 h-4 rounded-full flex items-center justify-center transform translate-x-1 -translate-y-1">{cartCount}</span>}</button>
                <button className="p-2 text-gray-600" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>{isMobileMenuOpen ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}</button>
            </div>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 absolute w-full left-0 z-50 shadow-2xl animate-fade-in">
            <div className="p-4 border-b border-gray-100">
               <AiSmartSearch />
            </div>
            <nav className="flex flex-col p-6 space-y-4 text-center max-h-[80vh] overflow-y-auto">
              {visibleHeaderPages.map(page => {
                 const productSections = page.sections.filter(s => s.type === SectionType.PRODUCT_GRID && s.title);
                 const hasDropdown = productSections.length > 1;
                 
                 if (!hasDropdown) return <Link key={page.id} to={page.slug} className="block py-2 text-sm uppercase tracking-widest text-gray-800 border-b border-transparent hover:border-gray-100" onClick={() => setIsMobileMenuOpen(false)}>{page.title}</Link>;
                 
                 return (
                    <div key={page.id} className="py-2">
                        <span className="block text-sm uppercase tracking-widest text-gray-900 font-bold mb-3">{page.title}</span>
                        <div className="flex flex-col gap-0 bg-gray-50 rounded border border-gray-100 overflow-hidden">
                            <Link 
                                to={page.slug} 
                                onClick={() => setIsMobileMenuOpen(false)} 
                                className="text-xs text-gray-500 italic py-3 px-4 border-b border-gray-200 hover:bg-gray-100 text-left"
                            >
                                Wszystkie
                            </Link>
                            {productSections.map(section => (
                                <button 
                                    key={section.id} 
                                    onClick={() => handleNavClick(page.slug, section.id)}
                                    className="text-left text-xs text-gray-700 uppercase tracking-wide py-3 px-4 hover:bg-white hover:text-black transition-colors border-b border-gray-100 last:border-0"
                                >
                                    {section.title}
                                </button>
                            ))}
                        </div>
                    </div>
                 );
              })}
            </nav>
          </div>
        )}
      </header>
      
      {/* CART OVERLAY */}
      {isCartOpen && (
          <div className="fixed inset-0 z-[100] flex justify-end">
              <div className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity" onClick={toggleCart}></div>
              <div className="relative bg-white w-full max-w-md h-full shadow-2xl flex flex-col animate-fade-in-right">
                  {/* Cart Header */}
                  <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                          {checkoutStep === 1 && <button onClick={() => setCheckoutStep(0)} className="text-gray-400 hover:text-black"><ArrowLeft size={20}/></button>}
                          <h2 className="font-serif text-2xl text-gray-900">{checkoutStep === 0 ? 'Twój Koszyk' : 'Dane Dostawy'}</h2>
                      </div>
                      <button onClick={toggleCart} className="text-gray-400 hover:text-black transition-colors"><X size={24} strokeWidth={1}/></button>
                  </div>

                  {/* Cart Items View (Step 0) */}
                  {checkoutStep === 0 && (
                      <>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {cart.length === 0 ? <p className="text-center text-gray-400 mt-10">Koszyk jest pusty</p> : (
                                cart.map(item => (
                                    <div key={`${item.id}-${Math.random()}`} className="flex gap-4">
                                        <div className="w-16 h-20 bg-gray-50 overflow-hidden flex-shrink-0 border border-gray-100">
                                            <img src={item.imageUrl} className="w-full h-full object-cover mix-blend-multiply"/>
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-serif text-sm text-gray-900 line-clamp-2">{item.title}</h3>
                                            {item.variantName && <p className="text-[10px] text-gray-500">{item.variantName}</p>}
                                            <div className="flex justify-between items-center mt-2">
                                                <p className="text-xs font-bold text-gray-900">{item.price} PLN</p>
                                                <p className="text-xs text-gray-500">x {item.quantity}</p>
                                            </div>
                                        </div>
                                        <button onClick={() => removeFromCart(item.id)} className="text-gray-400 hover:text-red-500 self-start"><Trash2 size={14}/></button>
                                    </div>
                                ))
                            )}
                        </div>
                        <AiCartAdvisor />
                        {cart.length > 0 && (
                            <div className="p-6 border-t border-gray-100 bg-gray-50">
                                <div className="space-y-2 mb-4">
                                    <div className="flex justify-between items-center text-sm text-gray-500">
                                        <span>Suma częściowa</span>
                                        <span>{cartSubtotalGross.toFixed(2)} PLN</span>
                                    </div>
                                    {discountAmount > 0 && (
                                        <div className="flex justify-between items-center text-sm font-bold text-green-600">
                                            <span>Rabat rabat kumulacyjny (-{discountPercent}%)</span>
                                            <span>-{discountAmount.toFixed(2)} PLN</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                        <span className="text-xs font-bold uppercase tracking-widest text-gray-900">Do zapłaty</span>
                                        <span className="text-lg font-serif">{cartSubtotalAfterDiscount.toFixed(2)} PLN</span>
                                    </div>
                                </div>
                                <button onClick={() => setCheckoutStep(1)} className="w-full bg-black text-white py-4 font-bold text-xs uppercase tracking-[0.2em] hover:bg-gray-800 transition-colors flex justify-center items-center gap-2">
                                    Przejdź do dostawy <ArrowRight size={14}/>
                                </button>
                            </div>
                        )}
                      </>
                  )}

                  {/* Checkout Form (Step 1) */}
                  {checkoutStep === 1 && (
                      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/50">
                          {/* Shipping Form */}
                          <div className="bg-white p-4 rounded shadow-sm border border-gray-100 space-y-3">
                              <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 flex items-center gap-2"><User size={14}/> Dane Kontaktowe</h4>
                              <div className="grid grid-cols-2 gap-2">
                                  <input placeholder="Imię" className="border rounded px-3 py-2 text-sm w-full" value={shippingData.firstName} onChange={e => setShippingData({...shippingData, firstName: e.target.value})} />
                                  <input placeholder="Nazwisko" className="border rounded px-3 py-2 text-sm w-full" value={shippingData.lastName} onChange={e => setShippingData({...shippingData, lastName: e.target.value})} />
                              </div>
                              <input placeholder="Email" type="email" className="border rounded px-3 py-2 text-sm w-full" value={shippingData.email} onChange={e => setShippingData({...shippingData, email: e.target.value})} />
                              <input placeholder="Telefon" className="border rounded px-3 py-2 text-sm w-full" value={shippingData.phone} onChange={e => setShippingData({...shippingData, phone: e.target.value})} />
                              
                              <input placeholder="Ulica i numer" className="border rounded px-3 py-2 text-sm w-full mt-2" value={shippingData.street} onChange={e => setShippingData({...shippingData, street: e.target.value})} />
                              <div className="grid grid-cols-3 gap-2">
                                  <input placeholder="Kod" className="border rounded px-3 py-2 text-sm w-full col-span-1" value={shippingData.zipCode} onChange={e => setShippingData({...shippingData, zipCode: e.target.value})} />
                                  <input placeholder="Miasto" className="border rounded px-3 py-2 text-sm w-full col-span-2" value={shippingData.city} onChange={e => setShippingData({...shippingData, city: e.target.value})} />
                              </div>
                          </div>

                          {/* Delivery Method */}
                          <div className="bg-white p-4 rounded shadow-sm border border-gray-100">
                              <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2"><Truck size={14}/> Sposób Dostawy</h4>
                              
                              <div className="space-y-2">
                                  <label className="flex items-center justify-between border rounded p-3 cursor-pointer hover:border-black transition-colors bg-white">
                                      <div className="flex items-center gap-3">
                                          <input type="radio" name="delivery" checked={deliveryMethod === 'courier'} onChange={() => setDeliveryMethod('courier')} className="accent-black"/>
                                          <div className="text-sm font-bold">Kurier (DPD/InPost)</div>
                                      </div>
                                      <div className="text-sm font-bold">{isFreeShipping ? '0.00 PLN' : `${SHIPPING_COST_COURIER} PLN`}</div>
                                  </label>

                                  <label className={`flex items-center justify-between border rounded p-3 cursor-pointer transition-colors bg-white ${isPaczkomatDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'hover:border-black'}`}>
                                      <div className="flex items-center gap-3">
                                          <input type="radio" name="delivery" checked={deliveryMethod === 'paczkomat'} onChange={() => !isPaczkomatDisabled && setDeliveryMethod('paczkomat')} disabled={isPaczkomatDisabled} className="accent-black"/>
                                          <div>
                                              <div className="text-sm font-bold">Paczkomat InPost</div>
                                              {isPaczkomatDisabled && <div className="text-[10px] text-red-500">Niedostępne dla gabarytów</div>}
                                          </div>
                                      </div>
                                      <div className="text-sm font-bold">{isFreeShipping ? '0.00 PLN' : `${SHIPPING_COST_PACZKOMAT} PLN`}</div>
                                  </label>
                              </div>

                              {deliveryMethod === 'paczkomat' && (
                                  <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                                      {paczkomatPoint ? (
                                          <div className="flex justify-between items-center">
                                              <div>
                                                  <p className="font-bold text-sm text-yellow-800">{paczkomatPoint.name}</p>
                                                  <p className="text-xs text-yellow-700">{paczkomatPoint.address}</p>
                                              </div>
                                              <button onClick={openGeowidget} className="text-xs bg-white border px-2 py-1 rounded">Zmień</button>
                                          </div>
                                      ) : (
                                          <button onClick={openGeowidget} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-2 rounded text-xs flex items-center justify-center gap-2">
                                              <MapPin size={14}/> Wybierz Paczkomat
                                          </button>
                                      )}
                                  </div>
                              )}
                          </div>

                          {/* Invoice Data */}
                          <div className="bg-white p-4 rounded shadow-sm border border-gray-100">
                               <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2"><FileText size={14}/> Dane do Rachunku</h4>
                               <div className="flex gap-2 mb-3">
                                   <button onClick={() => setDocumentType('receipt')} className={`flex-1 py-1 text-xs border rounded ${documentType === 'receipt' ? 'bg-black text-white border-black' : 'text-gray-500'}`}>Paragon</button>
                                   <button onClick={() => setDocumentType('invoice')} className={`flex-1 py-1 text-xs border rounded ${documentType === 'invoice' ? 'bg-black text-white border-black' : 'text-gray-500'}`}>Faktura</button>
                               </div>
                               {documentType !== 'receipt' && (
                                   <div className="space-y-2 animate-fade-in">
                                        <div className="flex gap-2">
                                            <input placeholder="NIP" className="border rounded px-3 py-2 text-sm w-full" value={invoiceData.nip} onChange={e => setInvoiceData({...invoiceData, nip: e.target.value})} />
                                            <button onClick={fetchCompanyData} disabled={isFetchingGus} className="bg-gray-100 px-3 py-2 rounded text-xs font-bold text-gray-600 hover:bg-gray-200">
                                                {isFetchingGus ? '...' : 'GUS'}
                                            </button>
                                        </div>
                                        {documentType === 'invoice' && (
                                            <>
                                                <input placeholder="Nazwa Firmy" className="border rounded px-3 py-2 text-sm w-full" value={invoiceData.companyName} onChange={e => setInvoiceData({...invoiceData, companyName: e.target.value})} />
                                                <input placeholder="Adres Firmy" className="border rounded px-3 py-2 text-sm w-full" value={invoiceData.address} onChange={e => setInvoiceData({...invoiceData, address: e.target.value})} />
                                            </>
                                        )}
                                   </div>
                               )}
                          </div>

                          {/* Summary */}
                          <div className="p-4 bg-gray-900 text-white rounded shadow-lg">
                              <div className="flex justify-between items-center text-sm mb-2">
                                  <span className="text-gray-400">Pozycje ({cartCount}):</span>
                                  <span>{cartSubtotalGross.toFixed(2)} PLN</span>
                              </div>
                              {discountAmount > 0 && (
                                  <div className="flex justify-between items-center text-sm mb-2 text-green-400">
                                      <span>Rabat ({discountPercent}%):</span>
                                      <span>-{discountAmount.toFixed(2)} PLN</span>
                                  </div>
                              )}
                              <div className="flex justify-between items-center text-sm mb-4">
                                  <span className="text-gray-400">Dostawa:</span>
                                  <span>{shippingCostGross === 0 ? <span className="text-green-400 font-bold uppercase tracking-widest text-[10px]">Darmowa</span> : `${shippingCostGross.toFixed(2)} PLN`}</span>
                              </div>
                              <div className="flex justify-between items-center text-xl font-bold pt-4 border-t border-gray-700">
                                  <span>Do zapłaty:</span>
                                  <span>{totalAmountGross.toFixed(2)} PLN</span>
                              </div>
                              <button 
                                  onClick={handleCheckout} 
                                  disabled={isPaymentProcessing}
                                  className="w-full bg-white text-black py-4 mt-6 font-bold text-xs uppercase tracking-[0.2em] hover:bg-gray-200 transition-colors flex justify-center items-center gap-2 disabled:opacity-50"
                              >
                                  {isPaymentProcessing ? 'Przetwarzanie...' : 'Zamawiam i Płacę'}
                              </button>
                          </div>
                      </div>
                  )}
              </div>
          </div>
      )}

      <main className="flex-grow bg-[#fcfcfc]">{children}</main>
      {isEditMode && isAdminLoggedIn && <EditControls />}
      <CelebrationBanner />
      <footer className="bg-[#1a1a1a] text-gray-400 py-16 border-t border-gray-800">
        <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12 text-sm">
          <div className="md:col-span-1"><span className="font-serif text-2xl text-white">{config.companyName}</span></div>
          <div><h3 className="text-white font-serif text-lg mb-6">Kontakt</h3><p>{config.contactEmail}</p><p>{config.contactPhone}</p></div>
          <div><h3 className="text-white font-serif text-lg mb-6">Informacje</h3>
              <ul className="space-y-2">
                  <li><Link to="/regulamin" className="hover:text-white transition-colors">Regulamin</Link></li>
                  <li><Link to="/polityka-prywatnosci" className="hover:text-white transition-colors">Polityka Prywatności</Link></li>
              </ul>
          </div>
        </div>
        <div className="container mx-auto px-6 mt-12 pt-8 border-t border-gray-800 text-xs text-gray-600 flex justify-between items-center relative z-20">
            <p>&copy; {new Date().getFullYear()} {config.companyName}. All rights reserved.</p>
        </div>
      </footer>
      <ImagePickerModal isOpen={isLogoModalOpen} onClose={() => setIsLogoModalOpen(false)} onSave={handleLogoSave} initialUrl={config.logoUrl} title="Zmień Logo" />
      <ProjectManager isOpen={isProjectManagerOpen} onClose={() => setIsProjectManagerOpen(false)} />
      <AIAssistant />
    </div>
  );
};

export default Layout;
